import { Component } from '@theme/component';

/**
 * Button Download Specs Component
 * Handles PDF generation and download for product specifications
 */
export class ButtonDownloadSpecs extends Component {
  /** @type {Promise<void>|null} */
  static _jspdfLoadPromise = null;

  /**
   * Dynamically load jsPDF library on-demand (only when user clicks download).
   * Avoids loading 150KB+ library on every product page load.
   * @returns {Promise<void>}
   */
  static loadJsPDF() {
    if (window.jspdf) return Promise.resolve();
    if (ButtonDownloadSpecs._jspdfLoadPromise) return ButtonDownloadSpecs._jspdfLoadPromise;

    ButtonDownloadSpecs._jspdfLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load jsPDF library'));
      document.head.appendChild(script);
    });

    return ButtonDownloadSpecs._jspdfLoadPromise;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleDownload.bind(this));
  }

  /**
   * Convert an image URL to Base64
   * @param {string} imgUrl - The image URL to convert
   * @returns {Promise<string>} - Promise resolving to base64 data URL
   */
  convertImageToBase64(imgUrl) {
    return new Promise((resolve, reject) => {
      if (!imgUrl || imgUrl.trim() === '') {
        reject(new Error('Invalid image URL: empty or undefined'));
        return;
      }

      let normalizedUrl = imgUrl.trim();
      if (normalizedUrl.startsWith('//')) {
        normalizedUrl = 'https:' + normalizedUrl;
      } else if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = window.location.origin + (normalizedUrl.startsWith('/') ? '' : '/') + normalizedUrl;
      }

      try {
        new URL(normalizedUrl);
      } catch (e) {
        reject(new Error(`Invalid image URL: ${imgUrl}`));
        return;
      }

      const image = new Image();
      image.crossOrigin = 'anonymous';
      
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          canvas.height = image.naturalHeight;
          canvas.width = image.naturalWidth;
          ctx.drawImage(image, 0, 0);
          const dataUrl = canvas.toDataURL();
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };
      
      image.onerror = () => reject(new Error(`Failed to load image: ${normalizedUrl}`));
      image.src = normalizedUrl;
    });
  }

  /**
   * Process product specifications data
   * @param {NodeList} allSpecsContainers - The specs container elements
   * @returns {{ usage: string, specs: string }} - Object containing usage and specs HTML
   */
  processSpecsData(allSpecsContainers) {
    const usageDataUl = document.createElement('ul');
    const specsDataUl = document.createElement('ul');

    allSpecsContainers.forEach((container) => {
      const _container = document.createElement('div');
      _container.innerHTML = container.innerHTML;
      
      _container.querySelectorAll('svg, script, iframe, object, embed').forEach(/** @param {Element} el */ (el) => el.remove());
      
      const usageData = _container.querySelectorAll('.product-spec--usage');
      const specsData = _container.querySelectorAll('.product-spec--specs');

      usageData.forEach((element) => {
        const clonedElement = element.cloneNode(true);
        clonedElement.classList.add('pdf-product-specs--item');
        
        const specTitle = clonedElement.querySelector('.product-spec--title');
        const specValue = clonedElement.querySelector('.product-spec--value');
        
        if (specTitle) specTitle.classList.add('pdf-product-specs--title');
        if (specValue) specValue.classList.add('pdf-product-specs--value');
        
        usageDataUl.appendChild(clonedElement);
      });

      specsData.forEach((element) => {
        const clonedElement = element.cloneNode(true);
        clonedElement.classList.add('pdf-product-specs--item');
        
        const specTitle = clonedElement.querySelector('.product-spec--title');
        const specValue = clonedElement.querySelector('.product-spec--value');
        
        if (specTitle) specTitle.classList.add('pdf-product-specs--title');
        if (specValue) specValue.classList.add('pdf-product-specs--value');
        
        specsDataUl.appendChild(clonedElement);
      });
    });

    return {
      usage: usageDataUl.innerHTML.replace(/Yes/g, 'Y').replace(/No/g, 'N'),
      specs: specsDataUl.innerHTML
    };
  }

  /**
   * Process product options data
   * @returns {Array<{label: string, items: Array<{title: string, imageUrl: string, isActive: boolean}>}>}
   */
  processProductOptions() {
    // Check for all product options elements (both regular and star-cross)
    const productOptionsScrollers = document.querySelectorAll('product-options-scroller, product-options-scroller-star-cross');
    
    if (productOptionsScrollers.length === 0) {
      return [];
    }
    
    const optionsGroups = [];
    
    productOptionsScrollers.forEach(scroller => {
      const labelElement = scroller.querySelector('.product-options__header .product-meta-variants__label');
      const label = labelElement?.textContent?.trim() || 'Options';
      const items = [];
      
      const optionWrappers = scroller.querySelectorAll('.product-options__wrapper');
      
      optionWrappers.forEach(wrapper => {
        // Look for both <a> and <button> elements
        const links = wrapper.querySelectorAll('a, button');
        
        links.forEach(element => {
          const img = element.querySelector('img');
          if (img && img.src) {
            items.push({
              title: element.getAttribute('aria-label') || '',
              imageUrl: img.src,
              isActive: element.classList.contains('active')
            });
          }
        });
      });
      
      if (items.length > 0) {
        optionsGroups.push({ label, items });
      }
    });
    
    return optionsGroups;
  }

  /**
   * Handle download button click
   * @param {Event} event - Click event
   */
  async handleDownload(event) {
    event.preventDefault();

    const loaderElement = document.querySelector('.download-spinner');

    try {
      loaderElement?.classList.remove('hidden');

      const filename = this.dataset.filename || 'product-specs.pdf';
      const productTitle = this.dataset.productTitle || '';
      const logoUrl = this.dataset.logoUrl || 'https://cdn.shopify.com/s/files/1/0456/8914/3460/files/logo.jpg?v=1706590146';
      const productImageUrl = this.dataset.productImage;
      const productSecondImageUrl = this.dataset.productSecondImage;

      const [logoBase64, img1Base64, img2Base64] = await Promise.all([
        this.convertImageToBase64(logoUrl),
        productImageUrl ? this.convertImageToBase64(productImageUrl) : Promise.resolve(null),
        productSecondImageUrl ? this.convertImageToBase64(productSecondImageUrl) : Promise.resolve(null)
      ]);

      const allSpecsContainers = document.querySelectorAll('.product-specs');
      const specs = allSpecsContainers.length > 0 ? this.processSpecsData(allSpecsContainers) : { usage: '', specs: '' };
      const productOptions = this.processProductOptions();

      console.log('Product options:', productOptions);

      // Dynamically load jsPDF library (only fetched on first click)
      await ButtonDownloadSpecs.loadJsPDF();

      // @ts-ignore
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        putOnlyUsedFonts: true,
        compress: true
      });

      // Set margins
      const marginLeft = 10;
      const marginRight = 10;
      const marginTop = 10;
      const pageWidth = 210 - marginLeft - marginRight;

      let yPosition = marginTop;

      // Add logo
      if (logoBase64) {
        doc.addImage(logoBase64, 'JPEG', marginLeft, yPosition, 50, 12);
      }
      yPosition += 18;
      
      // Add contact info
      doc.setFontSize(10);
      doc.text('1-833-845-3252 | tileclub.com', marginLeft, yPosition);
      yPosition += 10;

      // Add product title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text(productTitle, 105, yPosition, { align: 'center' });
      yPosition += 10;

      // Add product images
      if (img1Base64) {
        const imgWidth = img2Base64 ? 80 : 100;
        const imgX = img2Base64 ? marginLeft + 5 : marginLeft + 45;
        doc.addImage(img1Base64, 'JPEG', imgX, yPosition, imgWidth, imgWidth);
        
        if (img2Base64) {
          doc.addImage(img2Base64, 'JPEG', marginLeft + 95, yPosition, imgWidth, imgWidth);
        }
        yPosition += imgWidth + 10;
      }

      // Add product options
      if (productOptions && productOptions.length > 0) {
        for (const optionGroup of productOptions) {
          if (yPosition > 250) {
            doc.addPage();
            yPosition = marginTop;
          }
          
          doc.setFontSize(12);
          doc.setFont(undefined, 'bold');
          doc.text(optionGroup.label + ':', marginLeft, yPosition);
          yPosition += 5;
          
          // Convert all option images first
          const optionImages = [];
          for (const option of optionGroup.items) {
            if (option.imageUrl) {
              try {
                const imgBase64 = await this.convertImageToBase64(option.imageUrl);
                optionImages.push({ base64: imgBase64, isActive: option.isActive });
              } catch (e) {
                console.warn('Failed to load option image:', e);
              }
            }
          }
          
          // Display images in a row
          if (optionImages.length > 0) {
            let xPos = marginLeft;
            const imgSize = 15;
            const spacing = 3;
            
            for (const img of optionImages) {
              // Check if we need to wrap to next line
              if (xPos + imgSize > 210 - marginRight) {
                xPos = marginLeft;
                yPosition += imgSize + spacing;
                
                if (yPosition > 270) {
                  doc.addPage();
                  yPosition = marginTop;
                  xPos = marginLeft;
                }
              }
              
              doc.addImage(img.base64, 'JPEG', xPos, yPosition, imgSize, imgSize);
              
              xPos += imgSize + spacing;
            }
            
            yPosition += imgSize + 10;
          }
        }
      }

      // Add Usage section
      if (specs.usage) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('USAGE', marginLeft, yPosition);
        yPosition += 8;
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = specs.usage;
        const items = tempDiv.querySelectorAll('li');
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        let columnIndex = 0;
        let rowMaxHeight = 7;
        const itemsArray = Array.from(items);
        
        items.forEach((item, index) => {
          if (yPosition > 275) {
            doc.addPage();
            yPosition = marginTop;
            columnIndex = 0;
            rowMaxHeight = 7;
          }
          
          const title = item.querySelector('.pdf-product-specs--title')?.textContent?.trim() || '';
          const value = item.querySelector('.pdf-product-specs--value')?.textContent?.trim() || '';
          
          const xPos = columnIndex === 0 ? marginLeft : marginLeft + 90;
          const cellWidth = 95;
          
          // Check if value is too long and wrap text
          const maxWidth = cellWidth - 47;
          const valueLines = doc.splitTextToSize(value, maxWidth);
          const cellHeight = valueLines.length > 1 ? 7 + (valueLines.length - 1) * 4 : 7;
          
          // Calculate max height for the row (both cells)
          if (columnIndex === 0) {
            rowMaxHeight = cellHeight;
            // Check next item (second column) height
            if (index + 1 < itemsArray.length) {
              const nextValue = itemsArray[index + 1].querySelector('.pdf-product-specs--value')?.textContent?.trim() || '';
              const nextValueLines = doc.splitTextToSize(nextValue, maxWidth);
              const nextCellHeight = nextValueLines.length > 1 ? 7 + (nextValueLines.length - 1) * 4 : 7;
              rowMaxHeight = Math.max(rowMaxHeight, nextCellHeight);
            }
          }
          
          if (Math.floor(index / 2) % 2 === 0) {
            doc.setFillColor(238, 238, 238);
            doc.rect(xPos, yPosition - 4, cellWidth, rowMaxHeight, 'F');
          }
          
          doc.setFont(undefined, 'bold');
          doc.text(title + ':', xPos + 2, yPosition);
          doc.setFont(undefined, 'normal');
          
          if (valueLines.length > 1) {
            // Multi-line value
            doc.text(valueLines, xPos + 45, yPosition);
            yPosition += (valueLines.length - 1) * 4;
          } else {
            doc.text(value, xPos + 45, yPosition);
          }
          
          columnIndex++;
          if (columnIndex >= 2) {
            columnIndex = 0;
            yPosition += 7;
          }
        });
        
        if (columnIndex > 0) {
          yPosition += 7;
        }
        yPosition += 5;
      }

      // Add Product Specs section
      if (specs.specs) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('PRODUCT SPECS', marginLeft, yPosition);
        yPosition += 8;
        
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = specs.specs;
        const items = tempDiv.querySelectorAll('li');
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        
        let columnIndex = 0;
        let rowMaxHeight = 7;
        const itemsArray = Array.from(items);
        
        items.forEach((item, index) => {
          if (yPosition > 275) {
            doc.addPage();
            yPosition = marginTop;
            columnIndex = 0;
            rowMaxHeight = 7;
          }
          
          const title = item.querySelector('.pdf-product-specs--title')?.textContent?.trim() || '';
          const value = item.querySelector('.pdf-product-specs--value')?.textContent?.trim() || '';
          
          const xPos = columnIndex === 0 ? marginLeft : marginLeft + 90;
          const cellWidth = 95;
          
          // Check if value is too long and wrap text
          const maxWidth = cellWidth - 47;
          const valueLines = doc.splitTextToSize(value, maxWidth);
          const cellHeight = valueLines.length > 1 ? 7 + (valueLines.length - 1) * 4 : 7;
          
          // Calculate max height for the row (both cells)
          if (columnIndex === 0) {
            rowMaxHeight = cellHeight;
            // Check next item (second column) height
            if (index + 1 < itemsArray.length) {
              const nextValue = itemsArray[index + 1].querySelector('.pdf-product-specs--value')?.textContent?.trim() || '';
              const nextValueLines = doc.splitTextToSize(nextValue, maxWidth);
              const nextCellHeight = nextValueLines.length > 1 ? 7 + (nextValueLines.length - 1) * 4 : 7;
              rowMaxHeight = Math.max(rowMaxHeight, nextCellHeight);
            }
          }
          
          if (Math.floor(index / 2) % 2 === 0) {
            doc.setFillColor(238, 238, 238);
            doc.rect(xPos, yPosition - 4, cellWidth, rowMaxHeight, 'F');
          }
          
          doc.setFont(undefined, 'bold');
          doc.text(title + ':', xPos + 2, yPosition);
          doc.setFont(undefined, 'normal');
          
          if (valueLines.length > 1) {
            // Multi-line value
            doc.text(valueLines, xPos + 45, yPosition);
            yPosition += (valueLines.length - 1) * 4;
          } else {
            doc.text(value, xPos + 45, yPosition);
          }
          
          columnIndex++;
          if (columnIndex >= 2) {
            columnIndex = 0;
            yPosition += 7;
          }
        });
        
        if (columnIndex > 0) {
          yPosition += 7;
        }
      }

      doc.save(filename);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      loaderElement?.classList.add('hidden');
    }
  }
}

customElements.define('button-download-specs', ButtonDownloadSpecs);
